import Cross from "@icons/cross.svg?react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import isFinite from "lodash-es/isFinite";
import orderBy from "lodash-es/orderBy";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import invariant from "tiny-invariant";

import {
  canSignCheckFn,
  useGuard,
} from "@/common/acl/action-guard/action-guard";
import { BindingOfferSellDialog } from "@/common/components/binding-offer-dialog/binding-offer-sell-dialog";
import { Button, secondary } from "@/common/components/button/button";
import { InputField } from "@/common/components/form/input-field/input-field";
import { Metadata } from "@/common/components/metadata/metadata";
import {
  trackTradingSellCancelled,
  trackTradingSellClick,
  trackTradingSellConfirmed,
} from "@/common/components/mixpanel/mixpanel-tracking";
import * as OptionList from "@/common/components/option-list/option-list";
import { PropertiesList } from "@/common/components/properties-list/properties-list";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spacer } from "@/common/components/spacer/spacer";
import { useCurrency } from "@/common/hooks/use-currency";
import { useRequiredErpCompany } from "@/common/hooks/use-erp-company";
import { useSidebar } from "@/common/providers/sidebar-provider/sidebar-provider";
import { currencyFormatter } from "@/common/services/formatter";
import { getHarvestYearFromQuarter } from "@/common/utils/trading";
import { Currency } from "@/generated/digitalnisklady.cz/graphql";

import { useConfirmedAction } from "../admin/components/user/user";
import { FullscreenModalLayout } from "../layout/layout.fullscreen";
import { getAbsolutePath } from "../routes.utils";
import {
  cropPricesQuery,
  orderCropPrices,
  selectPricesByQuarter,
} from "../shared/api/crop-prices.api";
import { usePostalCodesQuery } from "../shared/api/get-postal-codes";
import { CropParameters } from "../shared/components/crop-parameters/crop-parameters";
import { usePostalCodeDialogParams } from "../shared/components/postal-code-dialog/postal-code-dialog";
import { path as failedPath } from "../shared/routes/failed/failed";
import { path as successPath } from "../shared/routes/success/success";
import { cropsQuery } from "../storages/api";
import { path as purchasePricesPath } from "./quick-trading";
import { useAddFastContractMutation } from "./quick-trading.detail.api";

const path = ":cropId/quarter/:quarterId";

type Fields = {
  marketPriceId: string;
  note: string;
  amount: number;
};

const QuickTradingDetail = () => {
  const { id: companyId } = useRequiredErpCompany();
  const { quarterId, cropId } = useParams();
  invariant(cropId);
  invariant(quarterId);
  const addFastContract = useAddFastContractMutation();
  const [, currency] = useCurrency();
  const { params } = usePostalCodeDialogParams();
  const postalCodes = useQuery(usePostalCodesQuery());
  const [searchParams] = useSearchParams();
  const postalCodeObject = postalCodes.data?.find(
    (postalCode) => postalCode.code === params?.postalCode,
  );
  const navigate = useNavigate();
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(postalCodeObject, "Postal code is not set");
  const postalCodeLabel = postalCodeObject.label;
  const prices = useSuspenseQuery({
    ...cropPricesQuery({
      companyId,
      cropId,
      currency: currency as Currency,
      latitude: postalCodeObject.latitude,
      locale: "cs",
      longitude: postalCodeObject.longitude,
    }),
    select: (data) =>
      orderBy(
        selectPricesByQuarter(orderCropPrices(data), quarterId),
        (item) => item.price,
        "desc",
      ),
  });
  const crops = useSuspenseQuery(cropsQuery());
  const firstMarketPriceId = prices.data.at(0)?.id;
  const defaultValues = React.useMemo(
    () => ({
      company: companyId,
      marketPriceId: firstMarketPriceId,
    }),
    [companyId, firstMarketPriceId],
  );
  const form = useForm<Fields>({
    defaultValues,
  });
  const formMarketPriceId = form.watch("marketPriceId");
  const currentCrop = crops.data.find((c) => c.id === cropId);
  const currentPrice =
    prices.data.find((p) => p.id === formMarketPriceId) ?? prices.data.at(0);
  const { runWhenConfirmed, dialog, setDialog, rejectRef, resolveRef } =
    useConfirmedAction();
  invariant(currentPrice);
  invariant(currentCrop);

  React.useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form, prices.data]);

  const handleSubmit = (data: Fields) => {
    trackTradingSellClick({
      crop: currentCrop.name,
      currency,
      price: currentPrice.price,
      quarter: currentPrice.quarter.name,
    });
    addFastContract.mutate(
      {
        amount: data.amount,
        companyId,
        cropId: currentCrop.id,
        currency: currency as Currency,
        gps: {
          latitude: postalCodeObject.latitude,
          longitude: postalCodeObject.longitude,
        },
        harvestYear: getHarvestYearFromQuarter(currentPrice.quarter.name),
        marketPriceId: currentPrice.id,
        note: data.note,
        zip: postalCodeObject.code,
      },
      {
        onError: async () => {
          setDialog(undefined);
          await navigate("sell/" + failedPath + "?" + searchParams.toString());
        },
        onSuccess: async (data) => {
          setDialog(undefined);
          await navigate(
            "sell/" +
              `${successPath}/${data.addFastContract?.id}?searchParams.toString()`,
          );
        },
      },
    );
  };

  const guardedSubmit = useGuard({
    action: handleSubmit,
    checkFn: canSignCheckFn,
  });

  const amount = form.watch("amount");
  const totalPrice = isFinite(amount)
    ? currencyFormatter(currency).format(currentPrice.price * amount)
    : undefined;

  return (
    <>
      <FullscreenModalLayout
        headerButton={
          <SearchParamsLink replace to={getAbsolutePath(purchasePricesPath)}>
            <Cross />
          </SearchParamsLink>
        }
      >
        <Metadata title={t`Prodat komoditu`} />
        <FullscreenModalLayout.Title>
          <Trans>Prodat komoditu</Trans>
        </FullscreenModalLayout.Title>
        <PropertiesList
          properties={[
            {
              label: <Trans>Komodita</Trans>,
              value: currentCrop.name,
            },
            {
              label: <Trans>PSČ</Trans>,
              value: postalCodeObject.label,
            },
            {
              label: <Trans>Kvartál</Trans>,
              value: currentPrice.quarter.name,
            },
            {
              label: <Trans>Celková cena</Trans>,
              value: totalPrice ?? "-",
            },
            {
              label: <Trans>Obchodník</Trans>,
              value: (
                <img
                  src={currentPrice.trader.logo}
                  style={{ height: "20px" }}
                />
              ),
            },
          ]}
        />

        <CropParameters cropId={cropId} traderId={currentPrice.trader.id} />

        <Spacer className="h-6" />
        <FormProvider {...form}>
          <form id="sell-form" onSubmit={form.handleSubmit(guardedSubmit)}>
            <fieldset disabled={false}>
              <InputField<Fields>
                autoComplete="off"
                autoFocus
                id="amount"
                label={<Trans>Množství v tunách</Trans>}
                name="amount"
                options={{
                  min: {
                    message: t`Množství musí být vyšší než 24 tun`,
                    value: 24,
                  },
                  required: t`Vyplňte prosím množství`,
                  valueAsNumber: true,
                }}
                step="0.1"
                type="number"
              />

              <Spacer className="h-6" />
              <label htmlFor="note">
                <Trans>Poznámka</Trans>
                <textarea
                  className="mt-2 w-full rounded-lg"
                  {...form.register("note")}
                  id="note"
                />
              </label>
              <Spacer className="h-4" />
              <h2 className="text-can-forest-teal font-bold">
                <Trans>Výběr obchodníka</Trans>
              </h2>
              <Spacer className="h-4" />
              <OptionList.List>
                {prices.data.map((price) => (
                  <OptionList.Item key={price.id}>
                    <OptionList.Label>
                      <OptionList.Checkbox
                        name="marketPriceId"
                        options={{
                          required: t`Vyberte prosím kvartál`,
                        }}
                        value={price.id}
                      >
                        <span>{price.quarter.name}</span>
                      </OptionList.Checkbox>
                      <span className="flex items-center">
                        <img
                          src={price.trader.logo}
                          style={{ height: "20px" }}
                        />
                      </span>

                      <b>{currencyFormatter(currency).format(price.price)}</b>
                    </OptionList.Label>
                  </OptionList.Item>
                ))}
              </OptionList.List>
            </fieldset>
          </form>
        </FormProvider>
        <Button.Container>
          <SearchParamsLink className={secondary} to="order">
            <Trans>Nastavit příkaz k prodeji</Trans>
          </SearchParamsLink>
          <Button
            disabled={addFastContract.status === "pending"}
            onClick={async () => {
              const isValid = await form.trigger();
              if (isValid) {
                runWhenConfirmed(
                  <Trans>
                    Přejete si potvrdit závaznou nabídku a zboží prodat?
                  </Trans>,
                  () => {
                    const formData = form.getValues();
                    guardedSubmit(formData);
                  },
                )();
              }
            }}
            type="button"
            variant="primary"
          >
            <Trans>
              Prodat za {currencyFormatter(currency).format(currentPrice.price)}
            </Trans>
          </Button>
        </Button.Container>
      </FullscreenModalLayout>
      {/* TODO the context is only needed for /order subroute */}
      <Outlet
        context={{
          amount: form.watch("amount"),
          crop: currentCrop,
          postalCodeObject: postalCodeObject,
          pricePerTon: currentPrice.price,
          quarter: currentPrice.quarter,
          traderId: currentPrice.trader.id,
          traders: prices.data.map((p) => p.trader),
        }}
      />
      {dialog ? (
        <BindingOfferSellDialog
          amount={form.getValues("amount") + " t"}
          cropName={currentCrop.name}
          isPending={addFastContract.status === "pending"}
          onCancel={() => {
            rejectRef.current?.();
            trackTradingSellCancelled({
              crop: currentCrop.name,
              currency,
              price: currentPrice.price,
              quarter: currentPrice.quarter.name,
            });
          }}
          onConfirm={(e) => {
            resolveRef.current?.(e);
            trackTradingSellConfirmed({
              crop: currentCrop.name,
              currency,
              price: currentPrice.price,
              quarter: currentPrice.quarter.name,
            });
          }}
          postalCodeLabel={postalCodeLabel}
          quarter={currentPrice.quarter.name}
          timeFromTrading={currentPrice.trader.timeFromTrading}
          timeToTrading={currentPrice.trader.timeToTrading}
          tocUrl={currentPrice.trader.tocUrl}
          traderName={currentPrice.trader.name}
          unitPrice={currencyFormatter(currency).format(currentPrice.price)}
        />
      ) : null}
    </>
  );
};

const useIsOpen = () => {
  const { cropId } = useParams();
  const sidebar = useSidebar();
  return !!(cropId || sidebar.state.screen);
};

export { path, QuickTradingDetail, useIsOpen };
